import type { TranslationKey } from '../i18n';
import type { BaseEntity, UUID } from './common';

export const STORE_PROMOTIONAL_AD_BUCKET = 'store-ad-media' as const;
export const MAX_PROMOTIONAL_AD_IMAGE_BYTES = 4 * 1024 * 1024;
export const MAX_PROMOTIONAL_ADS_PER_STORE = 10;
export const MAX_ACTIVE_PROMOTIONAL_ADS_PER_STORE = 5;
export const PROMOTIONAL_AD_IMAGE_MIN_WIDTH = 600;
export const PROMOTIONAL_AD_IMAGE_MAX_WIDTH = 2400;
export const PROMOTIONAL_AD_IMAGE_MIN_HEIGHT = 200;
export const PROMOTIONAL_AD_IMAGE_MAX_HEIGHT = 1200;
export const PROMOTIONAL_AD_MIN_ASPECT_RATIO = 1.8;
export const PROMOTIONAL_AD_MAX_ASPECT_RATIO = 4;

export const ACCEPTED_PROMOTIONAL_AD_IMAGE_MIME = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
} as const;

export type AcceptedPromotionalAdImageMime = keyof typeof ACCEPTED_PROMOTIONAL_AD_IMAGE_MIME;
export type PromotionalAdImageExtension =
  (typeof ACCEPTED_PROMOTIONAL_AD_IMAGE_MIME)[AcceptedPromotionalAdImageMime];

export type StorePromotionalAdSafeHref = string & {
  readonly __brand: 'StorePromotionalAdSafeHref';
};

export const PROMOTIONAL_AD_DESTINATION_TYPES = [
  'none',
  'sales',
  'category',
  'product',
  'external',
] as const;

export type PromotionalAdDestinationType = (typeof PROMOTIONAL_AD_DESTINATION_TYPES)[number];

export type StorePromotionalAdSafeExternalUrl = string & {
  readonly __brand: 'StorePromotionalAdSafeExternalUrl';
};

export type StorePromotionalAdDestination =
  | { readonly type: 'none' }
  | { readonly type: 'sales' }
  | { readonly type: 'category'; readonly categoryId: UUID }
  | { readonly type: 'product'; readonly productId: UUID }
  | { readonly type: 'external'; readonly url: StorePromotionalAdSafeExternalUrl };

export type PublicStorePromotionalAdDestination =
  | { readonly type: 'none' }
  | { readonly type: 'sales' }
  | { readonly type: 'category'; readonly categoryId: UUID | null }
  | { readonly type: 'product'; readonly productId: UUID | null }
  | { readonly type: 'external'; readonly url: StorePromotionalAdSafeExternalUrl };

export type AdminStorePromotionalAdDestination =
  | { readonly type: 'none' }
  | { readonly type: 'sales' }
  | {
      readonly type: 'category';
      readonly categoryId: UUID;
      readonly categoryName: string | null;
      readonly isAvailable: boolean;
    }
  | {
      readonly type: 'product';
      readonly productId: UUID;
      readonly productName: string | null;
      readonly categoryName: string | null;
      readonly isPublished: boolean | null;
      readonly available: number | null;
      readonly isAvailable: boolean;
    }
  | { readonly type: 'external'; readonly url: StorePromotionalAdSafeExternalUrl };

export interface StorePromotionalAd extends BaseEntity {
  readonly store_id: UUID;
  readonly title: string | null;
  readonly alt_text: string | null;
  readonly destination_type: PromotionalAdDestinationType;
  readonly destination_category_id: UUID | null;
  readonly destination_product_id: UUID | null;
  readonly destination_external_url: StorePromotionalAdSafeExternalUrl | null;
  readonly is_active: boolean;
  readonly display_order: number;
  readonly storage_path: string;
  readonly mime_type: AcceptedPromotionalAdImageMime;
  readonly file_size_bytes: number;
  readonly width_px: number;
  readonly height_px: number;
  readonly media_validated_at: string;
  readonly media_validated_by: 'store-ad-media-edge';
}

export interface StorePromotionalAdWithPreview extends StorePromotionalAd {
  readonly preview_url: string;
  readonly destination: AdminStorePromotionalAdDestination;
  readonly destinationSummary: PromotionalAdDestinationSummary;
}

export interface StorePromotionalAdStoreContext {
  readonly storeId: UUID;
  readonly storeSlug: string;
}

export interface PublicStorePromotionalAd {
  readonly id: UUID;
  readonly title: string | null;
  readonly alt_text: string | null;
  readonly destination: PublicStorePromotionalAdDestination;
  readonly display_order: number;
}

export interface PublicStorePromotionalAdWithUrl extends PublicStorePromotionalAd {
  readonly image_url: string;
  readonly href: StorePromotionalAdSafeHref | null;
  readonly isExternal: boolean;
}

export interface StorefrontBuilderPromotionalAdDestinationSummary {
  readonly key:
    | 'admin.promotionalAds.destination.summary.noLink'
    | 'admin.promotionalAds.destination.summary.sales'
    | 'admin.promotionalAds.destination.summary.category'
    | 'admin.promotionalAds.destination.summary.product'
    | 'admin.promotionalAds.destination.summary.external'
    | 'admin.promotionalAds.destination.summary.unavailable';
}

export interface StorefrontPublicPromotionalAdPickerOption {
  readonly kind: 'promotional-ad';
  readonly id: UUID;
  readonly title: string | null;
  readonly alt_text: string | null;
  readonly display_order: number;
  readonly image_url: string;
  readonly destinationSummaryKey: TranslationKey;
  readonly isSelectable: true;
}

export interface StorefrontResolvedSelectedPromotionalAd {
  readonly id: UUID;
  readonly title: string | null;
  readonly alt_text: string | null;
  readonly display_order: number;
  readonly image_url: string;
  readonly href: StorePromotionalAdSafeHref | null;
  readonly isExternal: boolean;
}

export interface PromotionalAdDestinationCategoryOption {
  readonly id: UUID;
  readonly name: string;
  readonly productCount: number;
  readonly isSelectable: boolean;
}

export interface PromotionalAdDestinationProductOption {
  readonly id: UUID;
  readonly name: string;
  readonly categoryId: UUID | null;
  readonly categoryName: string | null;
  readonly price_cents: number;
  readonly sale_price_cents: number | null;
  readonly currency: 'ILS';
  readonly published: boolean;
  readonly available: number;
  readonly isSelectable: boolean;
}

export interface PromotionalAdDestinationSummary {
  readonly key:
    | 'admin.promotionalAds.destination.summary.noLink'
    | 'admin.promotionalAds.destination.summary.sales'
    | 'admin.promotionalAds.destination.summary.category'
    | 'admin.promotionalAds.destination.summary.product'
    | 'admin.promotionalAds.destination.summary.external'
    | 'admin.promotionalAds.destination.summary.unavailable';
  readonly value?: string;
}

export interface PromotionalAdImageDimensions {
  readonly width: number;
  readonly height: number;
}

export type PromotionalAdImageValidationError =
  | 'imageRequired'
  | 'imageTooLarge'
  | 'imageInvalidType'
  | 'imageDimensionsInvalid';

export interface ValidatedPromotionalAdImage {
  readonly file: File;
  readonly mimeType: AcceptedPromotionalAdImageMime;
  readonly extension: PromotionalAdImageExtension;
  readonly width: number;
  readonly height: number;
}

export interface CreateStorePromotionalAdInput {
  readonly store_id: UUID;
  readonly store_slug: string;
  readonly title?: string | null;
  readonly alt_text?: string | null;
  readonly destination: StorePromotionalAdDestination;
  readonly is_active: boolean;
  readonly display_order?: number;
  readonly image: File;
}

export interface UpdateStorePromotionalAdInput {
  readonly ad_id: UUID;
  readonly store_id: UUID;
  readonly store_slug: string;
  readonly title?: string | null;
  readonly alt_text?: string | null;
  readonly destination?: StorePromotionalAdDestination;
  readonly is_active?: boolean;
  readonly image?: File | null;
}

export interface ValidatedPromotionalAdUploadResult {
  readonly ad: StorePromotionalAd;
  readonly preview_url: string;
}

export interface ReorderStorePromotionalAdsInput {
  readonly store_id: UUID;
  readonly ordered_ad_ids: readonly UUID[];
}

export interface DeleteStorePromotionalAdInput {
  readonly ad_id: UUID;
}

export type PromotionalAdErrorKey =
  | 'admin.promotionalAds.errors.mediaRequired'
  | 'admin.promotionalAds.errors.mediaInvalidType'
  | 'admin.promotionalAds.errors.mediaTooLarge'
  | 'admin.promotionalAds.errors.mediaDimensionsInvalid'
  | 'admin.promotionalAds.errors.titleTooLong'
  | 'admin.promotionalAds.errors.titleInvalid'
  | 'admin.promotionalAds.errors.altTooLong'
  | 'admin.promotionalAds.errors.altInvalid'
  | 'admin.promotionalAds.errors.destinationTypeRequired'
  | 'admin.promotionalAds.errors.categoryRequired'
  | 'admin.promotionalAds.errors.productRequired'
  | 'admin.promotionalAds.errors.externalHttpsRequired'
  | 'admin.promotionalAds.errors.destinationTargetUnavailable'
  | 'admin.promotionalAds.errors.tooManyAds'
  | 'admin.promotionalAds.errors.tooManyActiveAds'
  | 'admin.promotionalAds.errors.storageQuotaExceeded'
  | 'admin.entitlements.storageLimitBlocked'
  | 'admin.entitlements.promotionKitBlocked'
  | 'admin.promotionalAds.errors.permissionDenied'
  | 'admin.promotionalAds.errors.notFound'
  | 'admin.promotionalAds.errors.reorderInvalid'
  | 'common.errors.networkFailed'
  | 'common.errors.unknown';
